import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Code,
  Database,
  Headphones,
  Users,
  GraduationCap,
  Award,
  MapPin,
  DollarSign,
  Sparkles,
  Shield,
  ShieldCheck,
  ArrowRight,
  BookOpen,
  Heart,
  ShoppingCart,
  Factory,
  Building,
  Truck,
  Rocket,
  Star,
  CheckCircle2,
  Sun,
  Zap,
  Cpu,
  Camera
} from 'lucide-react';
import { services, whyChooseUs, industries, testimonials, faqs } from '../data/mock';
import FAQSection from '../components/FAQSection';

const iconMap = {
  Code,
  Database,
  Headphones,
  Users,
  GraduationCap,
  ShieldCheck,
  Award,
  MapPin,
  DollarSign,
  Sparkles,
  Shield,
  BookOpen,
  Heart,
  ShoppingCart,
  Factory,
  Building,
  Truck,
  Rocket,
  Sun,
  Zap,
  Cpu,
  Camera
};

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50 -z-10"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-8">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 text-sm font-medium">
                Empowering Digital Transformation
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Transforming Businesses with{' '}
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  Smart Technology
                </span>
                {' '}& Green Energy
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                Cosmos Next Gen Tech Pvt Ltd delivers cutting-edge software development, AI solutions, 
                enterprise systems, <strong>solar EPC & green energy solutions</strong>, EV charging infrastructure, 
                outsourced support, manpower solutions, and industry-ready training programs.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300">
                    Get Free Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/software-ai-services">
                  <Button variant="outline" className="px-8 py-6 text-lg rounded-lg font-medium border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-all duration-300">
                    Explore Services
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-blue-600">7+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">1000+</div>
                  <div className="text-sm text-gray-600">Projects Delivered</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">500+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
              </div>
            </div>

            {/* Right content - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1768661770207-9aa46d5ed526?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3NjI0NTg4NHww&ixlib=rb-4.1.0&q=85"
                  alt="Corporate Technology"
                  className="w-full h-auto"
                />
                {/* Glassmorphism overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-white/20 backdrop-blur-md border-t border-white/30 p-6">
                  <div className="flex items-center space-x-2 text-white">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Trusted by Leading Businesses</span>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-blue-600 to-cyan-500 text-white px-6 py-4 rounded-2xl shadow-xl">
                <div className="text-sm font-medium">Available 24/7</div>
                <div className="text-2xl font-bold">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Summary Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            About <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">CNGTPL</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            We are a next-generation technology and workforce solutions company committed to empowering businesses 
            through innovation, automation, and skilled talent. With a decade of experience, we serve clients across 
            India with comprehensive digital transformation services.
          </p>
          <Link to="/about">
            <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              Learn More About Us
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Our Services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Solutions for Your Business
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From software development to workforce solutions, we offer end-to-end services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 group bg-white"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  {service.external ? (
                    <a 
                      href={service.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={service.link} className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">CNGTPL</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine expertise, innovation, and dedication to deliver exceptional results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-6 rounded-xl hover:bg-blue-50 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Industries We Serve</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Serving Diverse Industries
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our solutions cater to various sectors with tailored approaches
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => {
              const Icon = iconMap[industry.icon];
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200 group"
                >
                  <Icon className="w-12 h-12 text-blue-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="font-semibold text-gray-900">{industry.name}</h3>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/industries">
              <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                View All Industries
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by businesses across India for quality and excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-8 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-blue-50">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  {testimonial.role && <div className="text-sm text-gray-600">{testimonial.role}</div>}
                  {testimonial.company && <div className="text-sm text-blue-600">{testimonial.company}</div>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a free consultation today and discover how we can help you grow
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300">
                Get Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919315649601">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg font-medium">
                Call Now: +91 9315649601
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection 
        faqs={faqs.general} 
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about our services"
      />
    </div>
  );
};

export default Home;
